import React, { useState, useEffect } from 'react';
import Loader from '../Loader';
import { RDS } from 'ouisys-engine/strategy';
import { SubmitUserDetailsFailure } from 'ouisys-engine/creditCardFlow';
import { Select, SelectType } from 'ouisys-component-library';
import { FormattedMessage, injectIntl } from '../../localization';
import { tracker } from 'ouisys-engine/utilities/tracker';
import { InputType } from 'ouisys-component-library';
import CreditCardInput from 'react-credit-card-input';
import './UserDetailsEntryStep.scss';
import { useRootContext } from '../../providers/RootContext';

import useFormData from './utils/useFormData';
import FormInput from './components/FormInput';
import defaultMessages from './utils/defaultMessages';
import payment from 'payment';
import isExpiryInvalid from './utils/isExpiryInvalid';

const UserDetailsEntryStep = ({ rds, onEnd, intl }) => {
  // Get the country param and set it as default on country input
  const queryParameters = new URLSearchParams(window.location.search);
  const initialCountry = (queryParameters.get('d_country') || '').toUpperCase();

  const [formData, handleChange, handleValidation, handleCreditCardError] = useFormData({
    email: { value: '', valid: true },
    cc_holder: { value: '', valid: true },
    cc_number: { value: '', valid: true },
    cc_expiry: { value: '', valid: true },
    cvv: { value: '', valid: true },
    country_code: { value: initialCountry, valid: true }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // fields and values
  const cvvIconSrc = require('../../assets/imgs/cvv-img.png');

  const handleFormEvents = (subscriptionState, input, interactionState, ev, value = '') => {
    if (ev.type === 'blur') {
      //                     /category                   // action                      // label         //args
      tracker.customEvent(`${subscriptionState}-state`, `${input}-${interactionState}`, `${input}-input`, value);
    }
    if (ev.type === 'click') {
      tracker.customEvent(`${subscriptionState}-state`, `${input + '-entry-started'}`, `${input}-input`, value);
    }
  };

  const handleEmailPaste = (ev) => {
    // setTimeout used to record pasted email value
    setTimeout(() => {
      tracker.customEvent('user-details-entry-state', 'copy-pasted', 'email-input', {
        msisdn: ev.target.value
      });
    });
  };

  const validateEmail = (ev) => {
    ev.persist();
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    const { value } = ev.target;
    const isValidEmail = emailRegex.test(value);
    handleChange(ev);
    handleValidation(ev, isValidEmail);
    const isEmailPasted = ev.nativeEvent?.inputType === 'insertFromPaste';
    // Significant length change could indicate autofill, and we check that it wasn't a paste action
    if (!isEmailPasted && value.includes('@') && value.length - formData.email.value > 1) {
      tracker.customEvent('user-details-entry-state', 'email-autofill', 'email-input', {
        email: value
      });
    }
    handleFormEvents('user-details-entry', 'email', `${isValidEmail ? 'valid' : 'invalid'}`, ev);
  };

  const handleCcNumberInput = (ev) => {
    handleChange(ev);
    handleValidation(ev);
    const ccNumberLength = ev.target.value.length;
    const isCcNumberPasted = ev.nativeEvent?.inputType === 'insertFromPaste';
    // ccNumberLength is 17 or 19 because it includes spaces.
    if (
      !isCcNumberPasted &&
      [17, 19].some((n) => n === ccNumberLength) &&
      ccNumberLength - formData.cc_number.value.length > 1
    ) {
      tracker.customEvent('cc-form-state', 'cc-number-autofill', 'cc-number-input');
    }
  };

  const handleInput = (ev, input, sendValue = false) => {
    ev.persist();
    handleChange(ev);
    handleValidation(ev);
    handleFormEvents(
      'cc-form',
      input,
      `${formData[ev.target.name].valid ? 'valid' : 'invalid'}`,
      ev,
      sendValue ? ev.target.value : ''
    );
  };

  const handleCardExpiryInput = (ev) => {
    const year = ev.target.value.split('/')[1];
    const yearDigits = year?.trim();
    // validation only triggers after both digits for year has been entered
    if (yearDigits?.length === 2) {
      if (isExpiryInvalid(ev.target.value.split(' / ').join('/'))) {
        handleCreditCardError('cc_expiry');
      } else {
        handleChange(ev);
        handleValidation(ev);
      }
    }
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!isFormComplete()) return;
    setIsSubmitting(true);
    const [month, year] = formData.cc_expiry.value.split('/');
    const data = {
      cc_exp_month: month.trim(),
      cc_exp_year: `20${year.trim()}`,
      first_name: 'default',
      last_name: 'default',
      address: 'default',
      postcode: 'default',
      city: 'default'
    };
    const userDetails = Object.keys(formData).reduce(
      (acc, key) => ({
        ...acc,
        [key]: formData[key].value
      }),
      data
    );
    onEnd(userDetails);
  };

  const renderCustomInput = (labelCopy, props, name, showCardTypes = false) => {
    const shouldShowCardTypes = !formData[name].value && showCardTypes;
    return (
      <>
        <p className="form-label">{labelCopy}</p>
        <input
          {...props}
          name={name}
          className={`${!formData[name].valid ? 'error-border' : ''}`}
          placeholder={shouldShowCardTypes ? '' : intl.formatMessage(defaultMessages[`${name}Placeholder`])}
        />
        {shouldShowCardTypes && <div className="card-types" />}
        <p className="error-msg">
          {!formData[name].valid &&
            intl.formatMessage(defaultMessages[name]) + ' ' + intl.formatMessage(defaultMessages.invalid)}
        </p>
      </>
    );
  };

  const isFormComplete = () => {
    return Object.keys(formData).every((key) => formData[key].value && formData[key].valid);
  };

  return (
    <div className="subscriptionArea">
      <button className="close-button" />
      <form autoComplete="on" onSubmit={handleSubmit}>
        <section className="ccDetails">
          <div className="box cc-wrap">
            {RDS.WhenLoading(null, () => <Loader />)(rds)}
            {RDS.WhenFailure(null, (err: SubmitUserDetailsFailure) => {
              console.log('the err', err);
              // deferring with setTimeout to prevent page crash
              setTimeout(() => {
                setIsSubmitting(false);
              });
              tracker.recedeInFlow('Flow', 'payment-submission-failed');
              return (
                <div className="errorMsg">
                  <FormattedMessage
                    id={err.errorId || err.errorType}
                    defaultMessage={defaultMessages?.[err?.errorId]?.defaultMessage || 'An unknown error occurred.'}
                  />
                  <br />
                  {err.errorType == 'AlreadySubscribed' && (
                    <a target="_blank" href={err.productUrl}>
                      <FormattedMessage id="goToProductLabel" defaultMessage="Go to Product" />
                    </a>
                  )}
                </div>
              );
            })(rds)}
            <div className="form-wrap">
              <div className="credit-card-form-wrapper">
                <div className="credit-card-form">
                  <FormInput
                    onClick={(ev) => {
                      handleFormEvents('user-details-entry', 'email', `entry-started`, ev);
                    }}
                    onBlur={validateEmail}
                    onChange={handleChange}
                    onPaste={handleEmailPaste}
                    name="email"
                    intl={intl}
                    formData={formData}
                    autoComplete="email"
                  />
                  <FormInput
                    labelId="cardHolderLabel"
                    onClick={(ev) => {
                      handleFormEvents('cc-form', 'cc-name', `entry-started`, ev);
                    }}
                    onBlur={(ev) => handleInput(ev, 'cc-name')}
                    onChange={(ev) => handleChange(ev)}
                    name="cc_holder"
                    intl={intl}
                    type={InputType.Text}
                    formData={formData}
                    autoComplete="cc-family-name"
                  />
                  <div className="form-group cc-mod-custom">
                    <label className="form-label" />
                    <CreditCardInput
                      cardNumberInputRenderer={({ props }) =>
                        renderCustomInput(
                          <FormattedMessage id="cardNumberLabel" defaultMessage="Card Number" />,
                          props,
                          'cc_number',
                          true
                        )
                      }
                      cardCVCInputRenderer={({ props }) => (
                        <div className="input-wrapper">
                          <div className="cvvHelp">
                            <input type="checkbox" id="cvvHelp" className="toggle" />
                            <div className="cvvHelpBox">
                              <img src={cvvIconSrc} />
                              <p>
                                <FormattedMessage
                                  id="cvvHelpMessage"
                                  defaultMessage="This is the 3 digit number on the back of your card."
                                />
                              </p>
                            </div>
                            <label htmlFor="cvvHelp">
                              <span>?</span>
                            </label>
                          </div>
                          {renderCustomInput(
                            <FormattedMessage id="cvvLabel" defaultMessage="Cvv Code" />,
                            props,
                            'cvv'
                          )}
                        </div>
                      )}
                      cardExpiryInputRenderer={({ props }) => (
                        <div className="input-wrapper">
                          {renderCustomInput(
                            <FormattedMessage id="expDate" defaultMessage="Expiry Date" />,
                            props,
                            'cc_expiry'
                          )}
                        </div>
                      )}
                      fieldClassName="cc-mod-control"
                      dangerTextClassName="hidden"
                      invalidClassName="error"
                      cardNumberInputProps={{
                        autoComplete: 'cc-number',
                        value: formData.cc_number.value,
                        onChange: handleCcNumberInput,
                        onError: () => {
                          handleCreditCardError('cc_number');
                        },
                        onBlur: (ev) => {
                          handleFormEvents(
                            'cc-form',
                            'cc-card-number',
                            `${payment.fns.validateCardNumber(ev.target.value) ? 'valid' : 'invalid'}`,
                            ev
                          );
                        },
                        onClick: (ev) => {
                          handleFormEvents('cc-form', 'cc-card-number', `entry-started`, ev);
                        }
                      }}
                      cardCVCInputProps={{
                        value: formData.cvv.value,
                        autoComplete: 'cc-cvv',
                        onChange: (ev) => {
                          handleChange(ev);
                          handleValidation(ev);
                        },
                        onError: () => {
                          handleCreditCardError('cvv');
                        },
                        onBlur: (ev) => {
                          handleFormEvents(
                            'cc-form',
                            'cc-cvv',
                            `${payment.fns.validateCardCVC(ev.target.value) ? 'valid' : 'invalid'}`,
                            ev
                          );
                        },
                        onClick: (ev) => {
                          handleFormEvents('cc-form', 'cc-cvv', `entry-started`, ev);
                        }
                      }}
                      cardExpiryInputProps={{
                        autoComplete: 'cc-exp',
                        onChange: handleCardExpiryInput,
                        onError: () => {
                          handleCreditCardError('cc_expiry');
                        },
                        onBlur: (ev) => {
                          handleFormEvents(
                            'cc-form',
                            'cc-expiry-input',
                            `${isExpiryInvalid(ev.target.value.split(' / ').join('/')) ? 'invalid' : 'valid'}`,
                            ev
                          );
                        },
                        onClick: (ev) => {
                          handleFormEvents('cc-form', 'cc-expiry-input', `entry-started`, ev);
                        }
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <FormattedMessage id="country" defaultMessage="Country" />
                    </label>
                    <Select
                      id={defaultMessages.country.id}
                      value={formData.country_code.value}
                      name="country_code"
                      onBlur={(ev) => handleInput(ev, 'country', true)}
                      onChange={handleChange}
                      required
                      placeholder={intl.formatMessage(defaultMessages.country)}
                      className="form-control"
                      error={
                        !formData.country_code.valid &&
                        intl.formatMessage(defaultMessages.country) + ' ' + intl.formatMessage(defaultMessages.required)
                      }
                      type={SelectType.Country}
                    />
                  </div>
                  <button
                    id="user-details-submit-button"
                    type="submit"
                    className="btn dcbp"
                    disabled={isSubmitting || !isFormComplete()}
                  >
                    <FormattedMessage id="finish" defaultMessage="finish" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
};

export default injectIntl(UserDetailsEntryStep);
